import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { FileText, Save, Check, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const SLUGS = ["terms", "privacy", "refund", "cookies"];

export default function AdminPolicies() {
  const { t } = useI18n();
  const [policies, setPolicies] = useState({});
  const [active, setActive] = useState("terms");
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/policies").then((r) => {
      const map = {};
      r.data.forEach((p) => { map[p.slug] = p; });
      setPolicies(map);
      if (map[active]) setDraft({ title: map[active].title, content: map[active].content });
    });
  }, []);  // eslint-disable-line

  useEffect(() => {
    if (policies[active]) setDraft({ title: policies[active].title, content: policies[active].content });
    setSaved(false);
    setError("");
  }, [active, policies]);

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(`/admin/policies/${active}`, draft);
      setPolicies((p) => ({ ...p, [active]: data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-policies">
      <div className="flex flex-wrap gap-2">
        {SLUGS.map((slug) => (
          <button
            key={slug}
            type="button"
            onClick={() => setActive(slug)}
            data-testid={`admin-policy-tab-${slug}`}
            className={`btn btn-sm ${active === slug ? "btn-primary" : "btn-secondary"}`}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={2} />
            {t(`admin_policy_${slug}`)}
          </button>
        ))}
      </div>

      <div className="surface-card p-6 space-y-5">
        <div>
          <label className="input-label">{t("admin_policy_title_label")}</label>
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="input"
            data-testid="admin-policy-title"
          />
        </div>

        <div>
          <label className="input-label">{t("admin_policy_content_label")}</label>
          <textarea
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            rows={20}
            className="input font-mono text-sm leading-relaxed"
            data-testid="admin-policy-content"
          />
          <p className="text-xs text-zinc-500 mt-2">{t("admin_policy_markdown_hint")}</p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3.5 py-3 text-sm">{error}</div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onSave} disabled={saving} data-testid="admin-policy-save" className="btn btn-primary disabled:opacity-60">
            {saved ? <Check className="w-4 h-4" strokeWidth={2.5} /> : <Save className="w-4 h-4" strokeWidth={2} />}
            {saving ? t("common_loading") : saved ? t("admin_policy_saved") : t("admin_policy_save")}
          </button>
          <Link to={`/legal/${active}`} target="_blank" className="btn btn-secondary btn-sm">
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
            {t("admin_policy_preview")}
          </Link>
          {policies[active]?.updated_at && (
            <span className="text-xs text-zinc-400 font-mono ms-auto">
              {t("admin_policy_updated")}: {policies[active].updated_at.split("T")[0]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
